import React, {Fragment} from 'react';

import {Link} from 'react-router';

import {Responsive} from 'app/general/mediaqueries';
import {AttachOverlay, PopoverForm} from 'app/general/overlays';
import {BreadcrumbItemStructure} from 'app/general/structures/BreadcrumbItemStructure';
import {getLocation} from 'app/utils/urls';

import type {OverlayProps} from 'app/general/overlays';
import type {FolderRecurrentParentSerializedType} from 'sweet-types';

type Props = {
  folder?: FolderRecurrentParentSerializedType;
};

const walkParentTree = (
  folder?: FolderRecurrentParentSerializedType | null
): Array<FolderRecurrentParentSerializedType> => {
  // walks up the parent tree and returns them from grandparent down.
  if (folder != null && folder.parent != null) {
    const parents = walkParentTree(folder.parent);
    return parents.concat([folder]);
  } else if (folder != null) {
    return [folder];
  } else {
    return [];
  }
};

export const DocumentFoldersBreadcrumbs = ({folder}: Props) => {
  return folder != null ? (
    <Responsive
      breakpoint="md"
      large={
        <Fragment>
          {walkParentTree(folder).map((f) => (
            <BreadcrumbItemStructure
              key={f.id}
              text={f.name}
              active={folder != null ? f.id === folder.id : false}
              shrinkText={true}
              link={f.html_url}
            />
          ))}
        </Fragment>
      }
      small={<MobileDocumentBreadcrumbs folder={folder} />}
    />
  ) : null;
};
type MobileDocumentBreadcrumbsProps = {
  folder: FolderRecurrentParentSerializedType;
};

function MobileDocumentBreadcrumbs({folder}: MobileDocumentBreadcrumbsProps) {
  const ancestors = walkParentTree(folder.parent);
  return (
    <Fragment>
      {ancestors.length > 0 ? (
        <BreadcrumbItemStructure
          text={
            <AttachOverlay
              trigger="click legacy"
              overlay={({overlayProps}) => (
                <AncestorFoldersPopover
                  folders={ancestors}
                  overlayProps={overlayProps}
                />
              )}
            >
              {(triggers) => (
                <button className="btn btn-link p-0" {...triggers}>
                  <i className="ml-1 fas fa-folders" />
                  <i className="ml-1 fas fa-caret-down" />
                </button>
              )}
            </AttachOverlay>
          }
          shrinkText={false}
        />
      ) : null}
      <BreadcrumbItemStructure
        text={folder.name}
        active={true}
        shrinkText={true}
        link={folder.html_url}
      />
    </Fragment>
  );
}

type AncestorFoldersPopoverProps = {
  overlayProps: OverlayProps;
  folders: Array<FolderRecurrentParentSerializedType>;
};

function AncestorFoldersPopover({
  folders,
  overlayProps,
}: AncestorFoldersPopoverProps) {
  return (
    <PopoverForm overlayProps={{...overlayProps, placement: 'bottom'}}>
      {({close}) => (
        <div
          className="py-2"
          style={{
            maxWidth: 250,
          }}
        >
          {folders.map((f) => (
            <Link
              key={f.id}
              className="d-flex px-3 py-1"
              to={getLocation(f.html_url)}
              onClick={() => close()}
            >
              <i className="flex-shrink-0 fa-fw fas fa-folder mr-1 mt-1" />
              {f.name}
            </Link>
          ))}
        </div>
      )}
    </PopoverForm>
  );
}
